import { Select, useBreakpointValue, useTheme, Box } from '@chakra-ui/react';
import React, { ChangeEvent } from 'react';
import { SortOption, sortOptions } from 'src/constants/sort-options';
import { ChevronDownIcon } from '@chakra-ui/icons';

export type SortOptionSelectProps = {
  value: SortOption | '';
  onChange?: (option: SortOption | null) => void;
  onBlur?: () => void;
  options: SortOption[];
};

const SortOptionSelect = ({ options, onChange, ...rest }: SortOptionSelectProps) => {
  const defaultOption = useBreakpointValue({ base: 'Sort', md: 'Default' }, { fallback: 'Sort' });
  const theme = useTheme();
  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;

    if (value === '') {
      onChange?.(null);
      return;
    }

    onChange?.(value as SortOption);
  };

  const buildThemeStyles = () => {
    const baseStyles = {
      fontFamily: theme.fonts.font_2,
      color: theme.colors.mainText,
      borderColor: theme.colors.gray[200],
      backgroundColor: 'white',
      _hover: {
        borderColor: theme.colors.gray[300],
      },
      _focus: {
        borderColor: theme.colors.primary[500],
        boxShadow: `0 0 0 1px ${theme.colors.primary[500]}`,
        backgroundColor: 'white',
      },
    };

    switch (theme.name) {
      case 'Magazine':
        return {
          ...baseStyles,
          color: theme.colors.primary[500],
          borderColor: theme.colors.primary[500],
        };
      default:
        return baseStyles;
    }
  };

  const themeStyles = buildThemeStyles();

  return (
    <Box position="relative" width="100%">
      <Select
        {...rest}
        onChange={handleChange}
        cursor="pointer"
        h="42px"
        {...themeStyles}
        icon={<Box />}
        sx={{
          option: { fontFamily: theme.fonts.font_2 },
          '& > option': { fontFamily: theme.fonts.font_2 },
        }}
      >
        <option value="">{defaultOption}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {sortOptions[opt]}
          </option>
        ))}
      </Select>
      <Box
        position="absolute"
        right="12px"
        top="50%"
        transform="translateY(-50%)"
        pointerEvents="none"
        zIndex={1}
        color={themeStyles.color}
      >
        <ChevronDownIcon boxSize={5} />
      </Box>
    </Box>
  );
};

export default SortOptionSelect;
